/**
 * FiltersBar — search (debounced by the page), role, shop, clear, and the
 * "N candidates" summary. Native selects keep arrow-key navigation free.
 */
import { Search, X } from 'lucide-react';
import { useShops } from '@/lib/shop-store';
import { useRoles } from './api';

const selectClass =
  'min-h-10 rounded-md border border-gray-200 bg-white px-2.5 text-[13px] text-gray-800 hover:border-gray-300';

export function FiltersBar({
  search,
  onSearch,
  roleId,
  onRole,
  shopId,
  onShop,
  onClear,
  hasActive,
  total,
  loading,
}: {
  search: string;
  onSearch: (v: string) => void;
  roleId: string;
  onRole: (v: string) => void;
  shopId: string;
  onShop: (v: string | null) => void;
  onClear: () => void;
  hasActive: boolean;
  total: number;
  loading: boolean;
}) {
  const { data: roles } = useRoles();
  const { data: shops } = useShops();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="relative min-w-0 flex-1 basis-[200px] sm:max-w-[280px]">
        <span className="sr-only">Search candidates</span>
        <Search
          size={15}
          strokeWidth={2}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500"
          aria-hidden
        />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search name, phone, email"
          className="block min-h-10 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2.5 text-[13px] text-gray-900 placeholder:text-gray-500 hover:border-gray-300"
        />
      </label>

      <label className="sr-only" htmlFor="filter-role">
        Role
      </label>
      <select id="filter-role" value={roleId} onChange={(e) => onRole(e.target.value)} className={selectClass}>
        <option value="">All roles</option>
        {(roles ?? [])
          .filter((r) => r.is_active === undefined || r.is_active === true || r.is_active === 1)
          .map((r) => (
            <option key={r.id} value={r.id}>
              {r.title}
            </option>
          ))}
      </select>

      <label className="sr-only" htmlFor="filter-shop">
        Shop
      </label>
      <select
        id="filter-shop"
        value={shopId}
        onChange={(e) => onShop(e.target.value || null)}
        className={selectClass}
      >
        <option value="">All shops</option>
        {(shops ?? []).map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>

      {hasActive && (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex min-h-10 items-center gap-1 rounded-md px-2.5 text-[13px] font-medium text-navy hover:bg-white"
        >
          <X size={14} strokeWidth={2} aria-hidden /> Clear
        </button>
      )}

      <span className="ml-auto text-[13px] tabular-nums text-muted" aria-live="polite">
        {loading ? 'Loading…' : `${total} candidate${total === 1 ? '' : 's'}`}
      </span>
    </div>
  );
}
